import React, { useCallback } from 'react';
import { noop } from 'lodash';
import propTypes from 'prop-types';
import classNames from 'classnames';
import EventsReporter from '../../events/reporter';
import { EventTypes } from '../../events/types';
import { LAYOUT_SOURCES } from '../../types/layoutSources';
import useImpressions from '../../hooks/useImpressions';
import ProfilePicture from '../ProfilePicture';
import NotificationIcon from '../../../NotificationsDrawer/Components/NotificationIcon';
import ItemUnreadCount from '../../../NotificationsDrawer/Components/ItemUnreadCount';
import ItemFooter from '../../../NotificationsDrawer/Components/ItemFooter';
import ItemContent from '../../../NotificationsDrawer/Components/ItemContent';
import PreviewImage from '../../../NotificationsDrawer/Components/PreviewImage';
import { SHARED_LOG_MODULE } from '../../../RealtimeNotifications/shared/constants';
import { SafeComponent } from '../../SafeComponent';
import './style.scss';
import { BUSINESS_CHAT_SUB_TYPE, PRO_PAYMENT_WAY_SUB_TYPE } from '../../../NotificationsDrawer/enums/consts';
import { triggerChat, triggerSeamlessPaymentMigrationModal } from '../../../NotificationsDrawer/utils';
import { DismissIcon } from './DismissIcon';

function ListItemComponent({ item, className, dismissable = false, layoutSource, onDismiss = noop, onClick = noop }) {
    const buildImpressionData = useCallback(() => ({ item, layoutSource }), [item, layoutSource]);

    const { containerRef } = useImpressions({
        buildImpressionData,
        eventKey: EventTypes.notification.shown,
    });

    const isToast = layoutSource === LAYOUT_SOURCES.TOAST;

    const itemClass = classNames('drawer-item', className, {
        toast: isToast,
        read: isToast ? false : item.isRead,
    });

    const handleClick = (e) => {
        customHandleClick(e);
        reportClick();
        onClick(e, item);
    };

    const customHandleClick = (e) => {
        const isTriggerType = [BUSINESS_CHAT_SUB_TYPE, PRO_PAYMENT_WAY_SUB_TYPE].includes(item.subType);

        if (item && item.subType && isTriggerType) {
            e.preventDefault();

            if (item.subType === BUSINESS_CHAT_SUB_TYPE) {
                triggerChat();
            }

            if (item.subType === PRO_PAYMENT_WAY_SUB_TYPE) {
                triggerSeamlessPaymentMigrationModal();
            }
        }
    };

    const reportClick = () => {
        EventsReporter.report(EventTypes.notification.clicked, {
            item,
            layoutSource,
        });
    };

    return (
        <li data-testid={item.id} className={itemClass} onClick={handleClick} ref={containerRef}>
            <a href={item.url} className="main-action" id={item.id}>
                <div className="image-wrapper">
                    {item.profilePicture && <ProfilePicture item={item} />}
                    {!item.profilePicture && <NotificationIcon item={item} />}
                </div>
                <div className="item-content-wrapper">
                    <ItemContent item={item} />
                    {!isToast && <ItemFooter item={item} />}
                </div>
                <div className="item-gutter">
                    {!isToast && (
                        <div className="controls">
                            <ItemUnreadCount item={item} layoutSource={layoutSource} />
                        </div>
                    )}
                    <div className="preview-image-wrapper">
                        <PreviewImage item={item} />
                    </div>
                </div>
            </a>
            {dismissable && <DismissButton onClick={onDismiss} />}
        </li>
    );
}

ListItemComponent.propTypes = {
    className: propTypes.string,
    item: propTypes.object,
    dismissable: propTypes.bool,
    layoutSource: propTypes.oneOf(Object.values(LAYOUT_SOURCES)),
    onDismiss: propTypes.func,
    onClick: propTypes.func,
};

function DismissButton({ onClick }) {
    return (
        <button data-testid="dismiss" className="dismiss" onClick={onClick}>
            <DismissIcon />
        </button>
    );
}

DismissButton.propTypes = {
    onClick: propTypes.func.isRequired,
};

export const ListItem = SafeComponent(SHARED_LOG_MODULE, 'ListItem')(ListItemComponent);
